import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from '@store/slices/authSlice'
import {
  Box,
  Typography,
  Card,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material'
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Snooze as SnoozeIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const Reminders = () => {
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedReminder, setSelectedReminder] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    priority: ''
  })

  useEffect(() => {
    if (token) {
      fetchReminders()
    } else {
      setError('Authentication token not found')
      setLoading(false)
    }
  }, [token, filters])

  const fetchReminders = async () => {
    try {
      console.log('🔄 Fetching reminders...')
      setLoading(true)
      setError(null)

      // Map frontend status values to backend values
      const mappedFilters = { ...filters }
      if (mappedFilters.status) {
        const statusMap = {
          'pending': 'PENDING',
          'sent': 'SENT', 
          'delivered': 'DELIVERED',
          'completed': 'COMPLETED',
          'failed': 'FAILED',
          'cancelled': 'CANCELLED',
          'snoozed': 'SNOOZED'
        }
        mappedFilters.status = statusMap[mappedFilters.status] || mappedFilters.status
      }

      const params = new URLSearchParams({
        page: 1,
        limit: 50,
        ...mappedFilters
      })

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/reminders?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.')
        } else {
          throw new Error(`Server error: ${response.status}`)
        }
      }
      
      const result = await response.json()
      console.log('📊 Reminders data received:', result)
      
      if (result.success) {
        // Normalize API shape to UI expectations
        const normalized = (result.reminders || []).map((r) => ({
          id: r.id,
          user: r.user?.phoneNumber || r.user?.id || '-',
          text: r.title || r.description || '',
          dueAt: r.scheduledAt,
          status: mapBackendStatusToFrontend(r.status),
          category: r.category || null,
          createdAt: r.createdAt,
        }))
        setReminders(normalized)
        setError(null)
      } else {
        throw new Error(result.message || 'Failed to fetch reminders')
      }
    } catch (error) {
      console.error('❌ Failed to fetch reminders:', error)
      setError(error.message || 'Failed to fetch reminders. Please try again.')
      setReminders([])
    } finally {
      setLoading(false)
    }
  }

  const mapBackendStatusToFrontend = (backendStatus) => {
    const statusMap = {
      'PENDING': 'pending',
      'SENT': 'sent',
      'DELIVERED': 'delivered',
      'COMPLETED': 'completed',
      'FAILED': 'failed',
      'CANCELLED': 'cancelled',
      'SNOOZED': 'snoozed'
    }
    return statusMap[backendStatus] || backendStatus?.toLowerCase() || 'pending'
  }



  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ search: '', status: '', category: '', priority: '' })
  }

  const handleViewReminder = (reminder) => {
    setSelectedReminder(reminder)
    setViewDialogOpen(true)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning'
      case 'sent': return 'info'
      case 'delivered': return 'success'
      case 'completed': return 'success'
      case 'failed': return 'error'
      case 'cancelled': return 'default'
      case 'snoozed': return 'secondary'
      default: return 'default'
    }
  }


  // Use reminders directly since filtering is done server-side
  const filteredReminders = reminders

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading reminders...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {t('reminders.title')}
            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {t('common.manageReminders')}
                </Typography>
          </Box>
        </Box>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3, p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap',
            '& > *': { minWidth: { xs: '100%', sm: 'auto' } }
          }}>
            <TextField
              label={t('common.search')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}
            />
            
            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', sm: 120 },
              flex: { xs: '1 1 100%', sm: '0 0 auto' }
            }}>
              <InputLabel>{t('reminders.status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('reminders.status')}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="pending">{t('status.pending')}</MenuItem>
                <MenuItem value="sent">{t('status.sent')}</MenuItem>
                <MenuItem value="delivered">{t('status.delivered')}</MenuItem>
                <MenuItem value="failed">{t('status.failed')}</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}
            >
              {t('common.clear')}
            </Button>
          </Box>
        </Card>
      </motion.div>

      {/* Reminders Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reminders.id')}</TableCell>
                  <TableCell>{t('users.user')}</TableCell>
                  <TableCell>{t('reminders.text')}</TableCell>
                  <TableCell>{t('reminders.dueAt')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReminders.map((reminder) => (
                  <TableRow key={reminder.id}>
                    <TableCell>{reminder.id}</TableCell>
                    <TableCell>{reminder.user}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>
                        {reminder.text}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {dayjs(reminder.dueAt).format('MMM DD, YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`status.${reminder.status}`)}
                        size="small"
                        color={getStatusColor(reminder.status)}
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                              <Tooltip title={t('common.view')}>
                        <IconButton 
                          size="small"
                          onClick={() => handleViewReminder(reminder)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </motion.div>

      {/* Reminder Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('reminders.reminderDetails')}</DialogTitle>
        <DialogContent>
          {selectedReminder && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.id')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedReminder.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('users.user')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedReminder.user}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.text')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedReminder.text}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.dueAt')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {dayjs(selectedReminder.dueAt).format('MMM DD, YYYY hh:mm A')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.timezone')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedReminder.timezone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Chip
                    label={t(`status.${selectedReminder.status}`)}
                    size="small"
                    color={getStatusColor(selectedReminder.status)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.category')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedReminder.category ? t(`categories.${selectedReminder.category}`) : t('common.notSpecified')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('reminders.createdAt')}
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(selectedReminder.createdAt).format('MMM DD, YYYY hh:mm A')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Reminders
